import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { formatPersonaScore } from '@/lib/personaSignalScore';

function formatSignalTypeLabel(signalType) {
  if (!signalType) return null;
  return signalType
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Shared signal card shell — matches the momentum / persona feed layout.
 */
export default function SignalsFeedCard({
  Icon,
  iconWrapClass = 'bg-gray-100',
  iconClass = 'text-gray-600',
  title,
  sourceUrl,
  linkAccent = 'decoration-[#00C2C2] hover:text-[#00C2C2]',
  score,
  signedDecimal = false,
  scoreWarning = false,
  meta = [],
  badges = [],
}) {
  const hasScore = score != null && score !== '' && !Number.isNaN(Number(score));
  const numericScore = hasScore ? Number(score) : null;
  const isPositive = numericScore != null ? numericScore >= 0 : true;

  return (
    <div className="block p-4 bg-white border rounded-lg hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {Icon ? (
            <div className={`p-2 rounded-full flex-shrink-0 ${iconWrapClass}`}>
              <Icon className={`w-5 h-5 ${iconClass}`} />
            </div>
          ) : null}
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 mb-1">
              {sourceUrl ? (
                <a
                  href={sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`font-semibold text-[#0A1A2F] break-words underline ${linkAccent}`}
                >
                  {title}
                </a>
              ) : (
                <h3 className="font-semibold text-[#0A1A2F] break-words leading-snug">{title}</h3>
              )}
              {sourceUrl ? <ExternalLink className="w-4 h-4 text-[#00C2C2] flex-shrink-0 mt-0.5" /> : null}
            </div>
            {meta.length > 0 ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#5C6B7A]">
                {meta.map((item, idx) => {
                  const content = (
                    <span className="inline-flex items-center gap-1 min-w-0">
                      {item.icon ? <item.icon className="w-4 h-4 flex-shrink-0" /> : null}
                      <span className="truncate">{item.label}</span>
                    </span>
                  );
                  if (item.href) {
                    return (
                      <Link key={idx} to={item.href} className="hover:text-[#00C2C2] min-w-0">
                        {content}
                      </Link>
                    );
                  }
                  return <div key={idx}>{content}</div>;
                })}
              </div>
            ) : null}
            {badges.length > 0 ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {badges.map((badge, idx) => (
                  <React.Fragment key={idx}>{badge}</React.Fragment>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        {hasScore ? (
          <div
            className={`text-lg font-bold flex-shrink-0 ${
              isPositive ? 'text-green-600' : scoreWarning ? 'text-amber-700' : 'text-red-600'
            }`}
          >
            {signedDecimal
              ? formatPersonaScore(numericScore)
              : (isPositive ? `+${numericScore}` : numericScore)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { formatSignalTypeLabel };
